import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCaseFile } from '../contexts/CaseFileContext';
import { UserPlus, FolderPlus, Users, ArrowRight, Check, AlertCircle, CheckCircle, RefreshCw, LogOut, Shield, Bell, Square, CheckSquare, ArrowLeft } from 'lucide-react';
// BETA-ONLY: Remove this import when beta ends
import { BetaProfileQuestionnaire } from '../features/beta-profile';
import { CURRENT_CONSENT_VERSION, PLATFORM_CONSENT_TEXT, RecordConsentResponse } from '../types/settings';
import { CaseFileCreationForm, CaseFileFormData, DesignatedAgentAuthMethod } from '../components/casefile';
import { InAppAuthorizationForm, UploadAuthorizationForm } from '../components/authorization';

// BETA-ONLY: Remove 'beta-profile' from this union when beta ends
type OnboardingStep = 'welcome' | 'beta-profile' | 'choice' | 'create' | 'join';

interface CreatedCaseFileInfo {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  careRecipientEmail?: string;
  authorizationMethod: DesignatedAgentAuthMethod;
}

interface CreateCaseFileResponse {
  success: boolean;
  caseFileId?: string;
  message?: string;
}

// Invitation validation response (matches backend response shape)
interface ValidateInvitationResponse {
  valid: boolean;
  caseFileName?: string;
  ownerName?: string;
  relationship?: string;
  role?: string;
  expiresAt?: string;
  error?: string;
}

// Structured invitation data for display
interface InvitationDisplayData {
  caseFileName: string;
  ownerName: string;
  relationship: string;
  role: string;
  inviteeName?: string;
  expiresAt: string;
}

// Invitation acceptance response
interface AcceptInvitationResponse {
  success: boolean;
  caseFileId?: string;
  caseFileName?: string;
  role?: string;
  message?: string;
}

interface ListUserCaseFilesResponse {
  success: boolean;
  caseFiles: Array<{
    caseFileId: string;
    name: string;
    isOwner: boolean;
  }>;
  totalCount: number;
}

interface PendingAuthorizationsResponse {
  success: boolean;
  authorizations: Array<{
    authorizationDocumentId: string;
    caseFileName: string;
    requestedBy: { name: string };
  }>;
  totalCount: number;
}

export function Onboarding(): JSX.Element {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { refreshCaseFiles } = useCaseFile();
  const [step, setStep] = useState<OnboardingStep>('welcome');

  // Consent state (shown before the CaseFile form)
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // Join CaseFile form state
  const [inviteCode, setInviteCode] = useState('');
  const [validatedInvitation, setValidatedInvitation] = useState<InvitationDisplayData | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingShared, setIsCheckingShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pending authorizations state
  const [pendingAuthCount, setPendingAuthCount] = useState(0);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Post-creation authorization state (for designated_agent with in_app/upload)
  const [createdCaseFile, setCreatedCaseFile] = useState<CreatedCaseFileInfo | null>(null);

  // Check for pending authorizations on mount
  useEffect(() => {
    const checkPendingAuthorizations = async () => {
      try {
        const getPendingAuthorizations = httpsCallable<
          Record<string, never>,
          PendingAuthorizationsResponse
        >(functions, 'getPendingAuthorizations');

        const result = await getPendingAuthorizations({});

        if (result.data.success) {
          setPendingAuthCount(result.data.totalCount);
        }
      } catch (err) {
        // Silently fail - this is not critical
        console.log('No pending authorizations or error checking:', err);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkPendingAuthorizations();
  }, []);

  const handleCreateCaseFile = async (formData: CaseFileFormData) => {
    setError(null);
    setConsentError(null);

    // Validate consent is checked
    if (!consentChecked) {
      setConsentError('You must agree to the data use consent to continue');
      throw new Error('Consent required');
    }

    setIsSubmitting(true);

    try {
      // First, record the consent
      const recordConsent = httpsCallable<
        { consentType: string; granted: boolean; version: string; consentText: string },
        RecordConsentResponse
      >(functions, 'recordConsent');

      const consentResult = await recordConsent({
        consentType: 'platform_consent',
        granted: true,
        version: CURRENT_CONSENT_VERSION,
        consentText: PLATFORM_CONSENT_TEXT,
      });

      if (!consentResult.data.success) {
        setError('Failed to record consent. Please try again.');
        throw new Error('Failed to record consent');
      }

      // Now create the CaseFile with the form data
      const createCaseFile = httpsCallable<
        CaseFileFormData,
        CreateCaseFileResponse
      >(functions, 'createCaseFile');

      const result = await createCaseFile(formData);

      if (result.data.success) {
        const authMethod = formData.designatedAgentData?.authorizationMethod;

        // For designated_agent with in-app or upload, show the form inline
        if (formData.authorityType === 'designated_agent' && authMethod && authMethod !== 'email') {
          setCreatedCaseFile({
            caseFileId: result.data.caseFileId!,
            name: formData.name,
            dateOfBirth: formData.dateOfBirth,
            careRecipientEmail: formData.designatedAgentData?.careRecipientEmail,
            authorizationMethod: authMethod,
          });
          return; // Don't navigate — show auth form
        }

        // Build success message based on authority type
        let message = `Welcome to Hesvara! CaseFile created for ${formData.name}.`;
        if (formData.authorityType === 'designated_agent' && authMethod === 'email') {
          message = `Welcome to Hesvara! CaseFile created for ${formData.name}. An authorization request has been sent to ${formData.designatedAgentData?.careRecipientEmail}. The CaseFile will remain locked until they authorize.`;
        } else if (formData.authorityType === 'legal_guardian') {
          message = `Welcome to Hesvara! CaseFile created for ${formData.name}. Please upload guardianship documentation to complete setup.`;
        } else if (formData.authorityType === 'parent_of_minor') {
          message = `Welcome to Hesvara! CaseFile created for ${formData.name}. You are authorized as their parent/guardian.`;
        }

        await refreshCaseFiles();
        navigate('/', {
          state: {
            message: message,
            caseFileId: result.data.caseFileId,
          },
        });
      } else {
        setError(result.data.message || 'Failed to create CaseFile');
        throw new Error(result.data.message || 'Failed to create CaseFile');
      }
    } catch (err) {
      console.error('Error creating CaseFile:', err);
      if (err instanceof Error && err.message !== 'Consent required' && err.message !== 'Failed to record consent') {
        setError(err.message || 'Failed to create CaseFile. Please try again.');
      } else if (!(err instanceof Error)) {
        setError('Failed to create CaseFile. Please try again.');
      }
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate invitation code without accepting
  const handleValidateInvitation = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invitation code');
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidatedInvitation(null);

    try {
      const validateInvitation = httpsCallable<{ invitationCode: string }, ValidateInvitationResponse>(
        functions,
        'validateCareTeamInvitation'
      );
      const result = await validateInvitation({ invitationCode: inviteCode.trim().toUpperCase() });

      if (result.data.valid) {
        // Map flat response to structured display data
        setValidatedInvitation({
          caseFileName: result.data.caseFileName || 'Unknown',
          ownerName: result.data.ownerName || 'Care Team Owner',
          relationship: result.data.relationship || 'other',
          role: result.data.role || 'viewer',
          expiresAt: result.data.expiresAt || '',
        });
      } else {
        setError(result.data.error || 'Invalid or expired invitation code');
      }
    } catch (err: unknown) {
      console.error('Error validating invitation:', err);
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.message) {
        const message = firebaseError.message.includes('invalid-argument')
          ? 'Invalid invitation code format'
          : firebaseError.message.includes('not-found')
          ? 'Invitation code not found or has expired'
          : firebaseError.message.replace(/^Firebase:\s*/, '').replace(/\s*\([^)]+\)\s*$/, '')
            || 'Failed to validate invitation code';
        setError(message);
      } else {
        setError('Failed to validate invitation code. Please try again.');
      }
    } finally {
      setIsValidating(false);
    }
  };

  // Accept the validated invitation
  const handleJoinCaseFile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatedInvitation) {
      setError('Please validate the invitation code first');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const acceptInvitation = httpsCallable<{ invitationCode: string }, AcceptInvitationResponse>(
        functions,
        'acceptCareTeamInvitation'
      );
      const result = await acceptInvitation({ invitationCode: inviteCode.trim().toUpperCase() });

      if (result.data.success) {
        await refreshCaseFiles();
        navigate('/', {
          state: {
            message: `Welcome to ${result.data.caseFileName || 'the care team'}! You've joined as ${result.data.role || 'a team member'}.`,
            caseFileId: result.data.caseFileId,
          },
        });
      } else {
        setError(result.data.message || 'Failed to accept invitation');
      }
    } catch (err: unknown) {
      console.error('Error accepting invitation:', err);
      const firebaseError = err as { code?: string; message?: string; details?: unknown };
      if (firebaseError.message) {
        const message = firebaseError.message
          .replace(/^Firebase:\s*/, '')
          .replace(/\s*\([^)]+\)\s*$/, '');
        setError(message || 'Failed to join care team. Please try again.');
      } else {
        setError('Failed to join care team. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset invitation state when code changes
  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value.toUpperCase();
    setInviteCode(newCode);
    // Clear validation when code changes
    if (validatedInvitation) {
      setValidatedInvitation(null);
    }
    setError(null);
  };

  const handleCheckSharedCaseFiles = async () => {
    setError(null);
    setIsCheckingShared(true);

    try {
      const listUserCaseFiles = httpsCallable<object, ListUserCaseFilesResponse>(
        functions,
        'listUserCaseFiles'
      );

      const result = await listUserCaseFiles({});

      if (result.data.success && result.data.totalCount > 0) {
        // User has access to CaseFiles - refresh context and redirect to dashboard
        const sharedCaseFiles = result.data.caseFiles.filter((cf) => !cf.isOwner);
        const message = sharedCaseFiles.length > 0
          ? `Found ${sharedCaseFiles.length} shared CaseFile(s) you have access to!`
          : `Found ${result.data.totalCount} CaseFile(s) you have access to!`;

        await refreshCaseFiles();
        navigate('/', {
          state: { message },
        });
      } else {
        setError(
          'No shared CaseFiles found. Ask the primary caregiver to add you to their care team, ' +
          'or create a new CaseFile to get started.'
        );
      }
    } catch (err) {
      console.error('Error checking shared CaseFiles:', err);
      if (err instanceof Error) {
        setError(err.message || 'Failed to check for shared CaseFiles. Please try again.');
      } else {
        setError('Failed to check for shared CaseFiles. Please try again.');
      }
    } finally {
      setIsCheckingShared(false);
    }
  };

  const getStepIndicator = () => {
    const steps = [
      { id: 'welcome', label: 'Welcome' },
      { id: 'beta-profile', label: 'About You' }, // BETA-ONLY: Remove when beta ends
      { id: 'choice', label: 'Choose Path' },
      { id: 'setup', label: 'Setup' },
    ];

    // BETA-ONLY: Remove 'beta-profile' case when beta ends
    const currentIndex = step === 'welcome' ? 0
      : step === 'beta-profile' ? 1
      : step === 'choice' ? 2
      : 3;

    return (
      <div className="flex items-center justify-center gap-2 mb-8">
        {steps.map((s, index) => (
          <div key={s.id} className="flex items-center">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${index < currentIndex ? 'bg-evergreen text-white' : ''}
                ${index === currentIndex ? 'bg-evergreen text-white ring-4 ring-evergreen/20' : ''}
                ${index > currentIndex ? 'bg-soft-neutral text-muted' : ''}
              `}
            >
              {index < currentIndex ? <Check className="w-4 h-4" /> : index + 1}
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-0.5 mx-1 ${
                  index < currentIndex ? 'bg-evergreen' : 'bg-soft-neutral'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderWelcomeStep = () => (
    <div className="text-center max-w-lg mx-auto">
      <div className="w-20 h-20 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <UserPlus className="w-10 h-10 text-evergreen" />
      </div>
      <h2 className="font-display text-3xl text-deep-forest mb-4">
        Welcome to Hesvara
      </h2>
      <p className="text-muted text-lg mb-2">
        Your caregiving advocacy companion
      </p>
      <p className="text-muted mb-8">
        Hesvara helps family caregivers manage complex care administration through
        AI-powered tools for document management, deadline tracking, and advocacy.
      </p>

      {/* Pending Authorization Alert */}
      {!isCheckingAuth && pendingAuthCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">
                You have {pendingAuthCount} pending authorization request{pendingAuthCount > 1 ? 's' : ''}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Someone has requested access to manage healthcare information on your behalf.
              </p>
              <button
                onClick={() => navigate('/authorization', { state: { fromOnboarding: true } })}
                className="mt-3 btn-secondary btn-sm bg-white hover:bg-amber-100"
              >
                Review Authorization Request{pendingAuthCount > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-airy rounded-lg p-6 mb-8">
        <p className="text-ink font-medium mb-2">
          Hi{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </p>
        <p className="text-muted text-sm">
          Let's get you set up. This will only take a few minutes.
        </p>
      </div>
      {/* BETA-ONLY: Change 'beta-profile' back to 'choice' when beta ends */}
      <button onClick={() => setStep('beta-profile')} className="btn-primary w-full sm:w-auto">
        Get Started <ArrowRight className="w-4 h-4 ml-2 inline" />
      </button>
      <div className="mt-6">
        <button
          onClick={() => signOut()}
          className="text-muted hover:text-ink text-sm underline"
        >
          Back to Login
        </button>
      </div>
    </div>
  );

  const renderChoiceStep = () => (
    <div className="max-w-3xl mx-auto">
      <h2 className="font-display text-2xl text-deep-forest text-center mb-2">
        How would you like to start?
      </h2>
      <p className="text-muted text-center mb-8">
        Choose the option that best describes your situation
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          className="card cursor-pointer hover:border-evergreen hover:shadow-md transition-all group"
          onClick={() => setStep('create')}
        >
          <div className="card-body pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-evergreen/20 transition-colors">
              <FolderPlus className="w-8 h-8 text-evergreen" />
            </div>
            <h3 className="font-semibold text-lg text-ink mb-2">
              Create a New CaseFile
            </h3>
            <p className="text-muted text-sm mb-4">
              I'm the primary caregiver and want to set up a new care profile
              for someone I care for.
            </p>
            <button className="btn-secondary text-sm group-hover:bg-evergreen group-hover:text-white group-hover:border-evergreen transition-colors">
              Create CaseFile <ArrowRight className="w-4 h-4 ml-1 inline" />
            </button>
          </div>
        </div>

        <div
          className="card cursor-pointer hover:border-brass hover:shadow-md transition-all group"
          onClick={() => setStep('join')}
        >
          <div className="card-body pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-brass/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-brass/20 transition-colors">
              <Users className="w-8 h-8 text-brass" />
            </div>
            <h3 className="font-semibold text-lg text-ink mb-2">
              Join an Existing Care Team
            </h3>
            <p className="text-muted text-sm mb-4">
              I've been added to someone else's care team and need to
              access their shared CaseFile.
            </p>
            <button className="btn-secondary text-sm group-hover:bg-brass group-hover:text-white group-hover:border-brass transition-colors">
              Join Team <ArrowRight className="w-4 h-4 ml-1 inline" />
            </button>
          </div>
        </div>

        <div
          className={`card cursor-pointer hover:border-blue-500 hover:shadow-md transition-all group ${
            pendingAuthCount > 0 ? 'ring-2 ring-amber-400' : ''
          }`}
          onClick={() => navigate('/authorization', { state: { fromOnboarding: true } })}
        >
          <div className="card-body pt-8 pb-8 text-center relative">
            {pendingAuthCount > 0 && (
              <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {pendingAuthCount}
              </div>
            )}
            <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/20 transition-colors">
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="font-semibold text-lg text-ink mb-2">
              Respond to Authorization
            </h3>
            <p className="text-muted text-sm mb-4">
              I received a request to authorize someone to access my
              healthcare information.
            </p>
            <button className="btn-secondary text-sm group-hover:bg-blue-500 group-hover:text-white group-hover:border-blue-500 transition-colors">
              {pendingAuthCount > 0 ? (
                <>Review {pendingAuthCount} Request{pendingAuthCount > 1 ? 's' : ''}</>
              ) : (
                <>Check for Requests</>
              )}
              <ArrowRight className="w-4 h-4 ml-1 inline" />
            </button>
          </div>
        </div>
      </div>

      <div className="text-center mt-8">
        <button
          onClick={() => signOut()}
          className="text-muted hover:text-ink text-sm underline"
        >
          Back to Login
        </button>
      </div>
    </div>
  );

  const renderCreateStep = () => (
    <div className="max-w-xl mx-auto">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
              <FolderPlus className="w-5 h-5 text-evergreen" />
            </div>
            <h2 className="text-xl font-semibold text-ink">Create Your First CaseFile</h2>
          </div>
          <p className="text-muted mt-2">
            Enter information about the person you're caring for. You can add more details later.
          </p>
        </div>

        <div className="card-body space-y-6">
          {/* Data Use Consent Section - shown first */}
          <div className="space-y-4">
            <h3 className="font-semibold text-ink border-b border-soft-neutral pb-2">
              Data Use Consent <span className="text-red-500">*</span>
            </h3>

            <div className="bg-airy rounded-lg p-4">
              <p className="text-sm text-ink leading-relaxed mb-4">
                {PLATFORM_CONSENT_TEXT}
              </p>
              <p className="text-xs text-muted mb-4">
                For more details, please review our{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-evergreen hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </p>

              <label
                className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${
                  consentChecked
                    ? 'border-evergreen bg-evergreen/5'
                    : consentError
                    ? 'border-red-300 bg-red-50'
                    : 'border-soft-neutral hover:border-evergreen/50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setConsentChecked(!consentChecked);
                    setConsentError(null);
                  }}
                  className="flex-shrink-0 mt-0.5"
                  disabled={isSubmitting}
                >
                  {consentChecked ? (
                    <CheckSquare className="w-5 h-5 text-evergreen" />
                  ) : (
                    <Square className="w-5 h-5 text-muted" />
                  )}
                </button>
                <span className="text-sm text-ink">
                  I agree to the data use consent above and understand how my health information will be used.
                </span>
              </label>

              {consentError && (
                <p className="error-text mt-2">{consentError}</p>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-error">{error}</div>
          )}

          {/* CaseFile Creation Form */}
          <CaseFileCreationForm
            onSubmit={handleCreateCaseFile}
            isLoading={isSubmitting}
            showInsurance={true}
            submitButtonText="Create CaseFile & Continue"
            showBackButton={true}
            onBack={() => setStep('choice')}
            backButtonText="Back"
          />
        </div>
      </div>
    </div>
  );

  const renderJoinStep = () => {
    // Helper to format role for display
    const formatRole = (role: string): string => {
      const roleMap: Record<string, string> = {
        admin: 'Administrator',
        editor: 'Editor',
        viewer: 'Viewer',
        contact: 'Contact',
      };
      return roleMap[role] || role;
    };

    // Helper to format relationship for display
    const formatRelationship = (relationship: string): string => {
      const relationshipMap: Record<string, string> = {
        self: 'Self (Care Recipient)',
        parent: 'Parent',
        child: 'Child',
        spouse: 'Spouse/Partner',
        sibling: 'Sibling',
        grandparent: 'Grandparent',
        extended: 'Extended Family',
        other: 'Other',
      };
      return relationshipMap[relationship] || relationship;
    };

    return (
    <div className="max-w-xl mx-auto">
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brass/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-brass" />
            </div>
            <h2 className="text-xl font-semibold text-ink">Join a Care Team</h2>
          </div>
          <p className="text-muted mt-2">
            If you've been added to a care team, you can access shared CaseFiles.
          </p>
        </div>

        <div className="card-body space-y-6">
          {/* Already Added Section */}
          <div className="bg-evergreen/5 border border-evergreen/20 rounded-lg p-4">
            <h3 className="font-semibold text-ink mb-2">Already been added to a care team?</h3>
            <p className="text-muted text-sm mb-4">
              If someone has already added you to their care team using your email address,
              click below to access your shared CaseFiles.
            </p>
            <button
              type="button"
              onClick={handleCheckSharedCaseFiles}
              disabled={isCheckingShared}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isCheckingShared ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Check for Shared CaseFiles
                </>
              )}
            </button>
          </div>

          {/* Invitation Code Section */}
          <div className="border-t border-soft-neutral pt-6">
            <form onSubmit={handleJoinCaseFile} className="space-y-6">
              {/* Invitation Code Input */}
              <div>
                <label htmlFor="inviteCode" className="label">
                  Invitation Code <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={handleInviteCodeChange}
                    className={`input flex-1 font-mono tracking-wider uppercase ${
                      validatedInvitation ? 'border-green-500 bg-green-50' : ''
                    }`}
                    placeholder="e.g., ABC12345"
                    maxLength={8}
                    disabled={isValidating || isSubmitting}
                  />
                  {!validatedInvitation && (
                    <button
                      type="button"
                      onClick={handleValidateInvitation}
                      disabled={!inviteCode.trim() || isValidating}
                      className="btn-secondary whitespace-nowrap"
                    >
                      {isValidating ? (
                        <>
                          <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                          Checking...
                        </>
                      ) : (
                        'Verify Code'
                      )}
                    </button>
                  )}
                </div>
                <p className="helper-text">
                  The code should be 8 characters (letters and numbers)
                </p>
              </div>

              {/* Validated Invitation Preview */}
              {validatedInvitation && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-800 mb-2">
                        Invitation Verified
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-green-700">CaseFile:</span>
                          <span className="font-medium text-green-900">
                            {validatedInvitation.caseFileName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-green-700">Invited by:</span>
                          <span className="font-medium text-green-900">
                            {validatedInvitation.ownerName || 'Care Team Owner'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-green-700">Your Role:</span>
                          <span className="font-medium text-green-900">
                            {formatRole(validatedInvitation.role)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-green-700">Relationship:</span>
                          <span className="font-medium text-green-900">
                            {formatRelationship(validatedInvitation.relationship)}
                          </span>
                        </div>
                        {validatedInvitation.inviteeName && (
                          <div className="flex justify-between">
                            <span className="text-green-700">Invited as:</span>
                            <span className="font-medium text-green-900">
                              {validatedInvitation.inviteeName}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-green-700">Expires:</span>
                          <span className="font-medium text-green-900">
                            {new Date(validatedInvitation.expiresAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setStep('choice');
                    setValidatedInvitation(null);
                    setInviteCode('');
                    setError(null);
                  }}
                  disabled={isSubmitting}
                  className="btn-secondary"
                >
                  Back
                </button>
                {validatedInvitation ? (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                        Joining...
                      </>
                    ) : (
                      <>
                        Join Care Team <ArrowRight className="w-4 h-4 ml-1 inline" />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setStep('create')}
                    className="btn-secondary flex-1"
                  >
                    Create New CaseFile Instead
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 text-center">
        <p className="text-muted text-sm">
          Don't have an invitation code?{' '}
          <button
            onClick={() => setStep('create')}
            className="text-evergreen hover:underline"
          >
            Create your own CaseFile
          </button>
        </p>
      </div>
    </div>
    );
  };

  // ===========================================================================
  // Post-creation authorization handlers
  // ===========================================================================

  const handleAuthorizationComplete = async () => {
    await refreshCaseFiles();
    navigate('/', {
      state: {
        message: `Welcome to Hesvara! CaseFile for ${createdCaseFile!.name} has been authorized and unlocked.`,
        caseFileId: createdCaseFile!.caseFileId,
      },
    });
  };

  const handleAuthorizationCancel = async () => {
    await refreshCaseFiles();
    navigate('/', {
      state: {
        message: `Welcome to Hesvara! CaseFile created for ${createdCaseFile!.name}. You can complete authorization from the CaseFile page.`,
        caseFileId: createdCaseFile!.caseFileId,
      },
    });
  };

  // ===========================================================================
  // Render: Post-creation authorization form (in-app or upload)
  // ===========================================================================

  if (createdCaseFile) {
    // In-App Authorization Form
    if (createdCaseFile.authorizationMethod === 'in_app') {
      return (
        <div className="min-h-screen bg-gradient-to-b from-airy to-white">
          <header className="bg-white/80 backdrop-blur border-b border-soft-neutral">
            <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
              <h1 className="font-display text-2xl text-deep-forest font-light">Hesvara</h1>
            </div>
          </header>
          <main className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={handleAuthorizationCancel}
              className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Skip — complete later
            </button>
            <InAppAuthorizationForm
              caseFileId={createdCaseFile.caseFileId}
              caseFileName={createdCaseFile.name}
              careRecipientName={createdCaseFile.name}
              careRecipientDob={createdCaseFile.dateOfBirth}
              careRecipientEmail={createdCaseFile.careRecipientEmail}
              onComplete={handleAuthorizationComplete}
              onCancel={handleAuthorizationCancel}
            />
          </main>
        </div>
      );
    }

    // Upload Authorization Form
    if (createdCaseFile.authorizationMethod === 'upload') {
      return (
        <div className="min-h-screen bg-gradient-to-b from-airy to-white">
          <header className="bg-white/80 backdrop-blur border-b border-soft-neutral">
            <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
              <h1 className="font-display text-2xl text-deep-forest font-light">Hesvara</h1>
            </div>
          </header>
          <main className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={handleAuthorizationCancel}
              className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Skip — complete later
            </button>
            <UploadAuthorizationForm
              caseFileId={createdCaseFile.caseFileId}
              caseFileName={createdCaseFile.name}
              careRecipientName={createdCaseFile.name}
              careRecipientDob={createdCaseFile.dateOfBirth}
              careRecipientEmail={createdCaseFile.careRecipientEmail}
              onComplete={handleAuthorizationComplete}
              onCancel={handleAuthorizationCancel}
            />
          </main>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-airy to-white">
      <header className="bg-white/80 backdrop-blur border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="font-display text-2xl text-deep-forest font-light">
            Hesvara
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted hidden sm:inline">
              {user?.email}
            </span>
            <button
              onClick={signOut}
              className="flex items-center gap-2 text-sm text-muted hover:text-ink transition-colors"
              title="Sign out and use a different account"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Change User</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {getStepIndicator()}

        {step === 'welcome' && renderWelcomeStep()}
        {/* BETA-ONLY: Remove this step when beta ends */}
        {step === 'beta-profile' && (
          <BetaProfileQuestionnaire
            onComplete={() => setStep('choice')}
            onBack={() => setStep('welcome')}
          />
        )}
        {step === 'choice' && renderChoiceStep()}
        {step === 'create' && renderCreateStep()}
        {step === 'join' && renderJoinStep()}
      </main>
    </div>
  );
}

export default Onboarding;
